import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  highlight: "var(--bc-panel-elev, #27272A)",
  text: "var(--bc-ink, #FFFFFF)",
  muted: "var(--bc-sub, #71717A)",
  cyan: "#06B6D4",
};

const NAV_KEY = "bc_match_nav";

export function saveMatchNav(ids, currentIdx) {
  try {
    sessionStorage.setItem(NAV_KEY, JSON.stringify({ ids, currentIdx }));
  } catch { /* quota / serialization errors are safe to ignore */ }
}

function readNav() {
  try {
    return JSON.parse(sessionStorage.getItem(NAV_KEY) || "null");
  } catch {
    return null;
  }
}

/* Fallback: build a nav list so arrows show even when the user lands
 * on /match/<id> directly (deep link, refresh, shared URL, opening from
 * a non-HomePage route). Strategy: pull today's fixtures from
 * ``/api/fixtures/today`` (always populated, with kickoff datetime) and
 * sort by kickoff time → consistent with HomePage default order.
 */
async function buildFallbackNav(currentId) {
  try {
    const { data } = await axios.get(`${API}/fixtures/today`, { timeout: 8000 });
    const leagues = data?.leagues || [];
    const flat = [];
    for (const lg of leagues) {
      for (const m of (lg.matches || [])) {
        const id = Number(m.id);
        if (!Number.isFinite(id)) continue;
        const ts = m.date ? Date.parse(m.date) : 0;
        flat.push({ id, ts });
      }
    }
    flat.sort((a, b) => a.ts - b.ts);
    const ids = flat.map((x) => x.id);
    if (ids.length < 2) return null;
    const idx = ids.indexOf(Number(currentId));
    if (idx < 0) {
      // Current fixture not in today's list (e.g. yesterday's
      // result page) — prepend so arrows still allow navigation
      // forward into today's matches.
      ids.unshift(Number(currentId));
      return { ids, currentIdx: 0 };
    }
    return { ids, currentIdx: idx };
  } catch {
    return null;
  }
}

export default function MatchNavArrows({ fixtureId, onNavigate }) {
  const [nav, setNav] = useState(() => readNav());

  // If nav is missing OR current fixture is not in the list, build a
  // fallback from /api/predictions-summary so the arrows always work.
  useEffect(() => {
    const fid = Number(fixtureId);
    if (!Number.isFinite(fid)) return;
    const current = readNav();
    if (current && Array.isArray(current.ids) && current.ids.indexOf(fid) >= 0 && current.ids.length >= 2) {
      // Existing nav is usable — sync currentIdx if drifted.
      const realIdx = current.ids.indexOf(fid);
      if (realIdx !== current.currentIdx) {
        saveMatchNav(current.ids, realIdx);
        setNav({ ids: current.ids, currentIdx: realIdx });
      } else {
        setNav(current);
      }
      return;
    }
    let cancelled = false;
    buildFallbackNav(fid).then((fb) => {
      if (cancelled || !fb) return;
      saveMatchNav(fb.ids, fb.currentIdx);
      setNav(fb);
    });
    return () => { cancelled = true; };
  }, [fixtureId]);

  const go = useCallback((targetId, newIdx) => {
    if (targetId == null || !nav) return;
    saveMatchNav(nav.ids, newIdx);
    onNavigate(targetId);
  }, [nav, onNavigate]);

  // Compute prev/next ids (memoised via render — cheap)
  const idx = nav && Array.isArray(nav.ids) ? nav.ids.indexOf(Number(fixtureId)) : -1;
  const prevId = idx > 0 ? nav.ids[idx - 1] : null;
  const nextId = nav && idx >= 0 && idx < nav.ids.length - 1 ? nav.ids[idx + 1] : null;

  // Keyboard shortcuts ← / → (ignored when typing inside a form field).
  useEffect(() => {
    const handler = (e) => {
      // Skip when modifier keys are pressed (preserve browser shortcuts)
      if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      if (e.key === "ArrowLeft" && prevId != null) {
        e.preventDefault();
        go(prevId, idx - 1);
      } else if (e.key === "ArrowRight" && nextId != null) {
        e.preventDefault();
        go(nextId, idx + 1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [prevId, nextId, idx, go]);

  if (!nav || !Array.isArray(nav.ids) || nav.ids.length < 2 || idx < 0) return null;

  return (
    <>
      {/* Prev arrow — left edge */}
      {prevId != null && (
        <button
          data-testid="match-nav-prev"
          aria-label="Incontro precedente (← freccia sinistra)"
          onClick={() => go(prevId, idx - 1)}
          style={{
            position: "fixed",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 40,
            width: 44,
            height: 64,
            background: "rgba(9,9,11,0.85)",
            border: `1px solid ${C.highlight}`,
            borderLeft: `2px solid ${C.cyan}`,
            color: C.text,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.15s ease",
            backdropFilter: "blur(8px)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(6,182,212,0.15)";
            e.currentTarget.style.color = C.cyan;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(9,9,11,0.85)";
            e.currentTarget.style.color = C.text;
          }}
          title="Incontro precedente (←)"
        >
          <ChevronLeft size={20} />
        </button>
      )}

      {/* Next arrow — right edge */}
      {nextId != null && (
        <button
          data-testid="match-nav-next"
          aria-label="Incontro successivo (→ freccia destra)"
          onClick={() => go(nextId, idx + 1)}
          style={{
            position: "fixed",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 40,
            width: 44,
            height: 64,
            background: "rgba(9,9,11,0.85)",
            border: `1px solid ${C.highlight}`,
            borderRight: `2px solid ${C.cyan}`,
            color: C.text,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.15s ease",
            backdropFilter: "blur(8px)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(6,182,212,0.15)";
            e.currentTarget.style.color = C.cyan;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(9,9,11,0.85)";
            e.currentTarget.style.color = C.text;
          }}
          title="Incontro successivo (→)"
        >
          <ChevronRight size={20} />
        </button>
      )}

      {/* Position indicator + keyboard hint (small, bottom centre) */}
      <div
        data-testid="match-nav-indicator"
        style={{
          position: "fixed",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 40,
          background: "rgba(9,9,11,0.9)",
          border: `1px solid ${C.highlight}`,
          padding: "4px 12px",
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: 10,
          color: C.muted,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          backdropFilter: "blur(8px)",
          pointerEvents: "none",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ color: C.cyan }}>{idx + 1}</span>
        <span>/{nav.ids.length}</span>
        <span style={{ opacity: 0.55, marginLeft: 6 }}>← →</span>
      </div>
    </>
  );
}
